import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useByoFilter from '@apiHooks/useByoFilter';
import useCustomRestaurantLists from '@apiHooks/useCustomRestaurantLists';
import { components } from '@api/api';
import { currentCountrySelector } from "@selectors/countries.selector";
import Loader from "@components/Loader/Loader";
import { RegionsEnum, SlicesEnum } from "@models/ByoTableData";

type MainFilterOutput = components['schemas']['Application.Common.Models.MainFilterOutput'];
type RestaurantList = components['schemas']['Application.Common.Models.MainFilterOutput.RestaurantList'];
export type CustomRestaurantList = components['schemas']['MenuTrends.Application.Contracts.Models.Restaurants.CustomRestaurantList.CustomRestaurantListViewModel'];
type CustomRestaurantListDetails = components['schemas']['MenuTrends.Application.Contracts.Models.Restaurants.CustomRestaurantList.CustomRestaurantListDetailsViewModel'];
export type RestaurantMenuContextType = {
    restaurantLists: RestaurantList[];
    rawFilterData: MainFilterOutput;
    tooltipsData: Array<{
        sliceId: SlicesEnum;
        id: number;
        title?: string;
        description?: string;
        imageUrl?: string;
    }>;
    customRestaurantLists: CustomRestaurantList[];
    activeRestaurantListId: number;
    isLoadingRestaurantLists: boolean;
    isLoadingCustomRestaurantLists: boolean;
    setActiveRestaurantList: (id: number) => void;
    createCustomRestaurantList: (data: CustomRestaurantListDetails) => Promise<void>;
    updateCustomRestaurantList: (data: CustomRestaurantListDetails) => Promise<void>;
    deleteCustomRestaurantList: (countryCode: string, id: number) => Promise<void>;
}

export const defaultContextValue: RestaurantMenuContextType = {
    restaurantLists: [],
    rawFilterData: {},
    tooltipsData: [],
    customRestaurantLists: [],
    activeRestaurantListId: 0,
    isLoadingRestaurantLists: true,
    isLoadingCustomRestaurantLists: true,
    setActiveRestaurantList: () => null,
    createCustomRestaurantList: () => Promise.resolve(),
    updateCustomRestaurantList: () => Promise.resolve(),
    deleteCustomRestaurantList: () => Promise.resolve(),
};

export const RestaurantMenuContext = createContext<RestaurantMenuContextType>(defaultContextValue);

const RestaurantMenuProvider: FC = ({ children }) => {
    const countryCode = useSelector(currentCountrySelector);

    const {
        data: filterData,
        isLoading: isLoadingRestaurantLists,
    } = useByoFilter(countryCode);

    const {
        data: customRestaurantListsData,
        isLoading: isLoadingCustomRestaurantLists,
        createList,
        updateList,
        deleteList,
    } = useCustomRestaurantLists(countryCode);

    const restaurantLists = useMemo(
        () => (filterData?.restaurantLists ?? []).filter((item) => !item.isCustom) ?? [],
        [filterData?.restaurantLists]
    );

    const tooltipsData = useMemo(
        (): RestaurantMenuContextType['tooltipsData'] => [
            ...(filterData?.tooltips ?? []).map(i => ({
                sliceId: i.slice as SlicesEnum,
                id: i.id ?? 0,
                title: i.definition ?? '',
                description: `Examples: ${i.examples ?? ''}`,
            })),
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.West,
                imageUrl: '/West.svg',
            },
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.Midwest,
                imageUrl: '/Midwest.svg',
            },
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.South,
                imageUrl: '/South.svg',
            },
            {
                sliceId: SlicesEnum.Regions,
                id: RegionsEnum.NorthEast,
                imageUrl: '/Northeast.svg',
            },
        ],
        [filterData?.tooltips]
    );

    const customRestaurantLists = useMemo(
        () => customRestaurantListsData ?? [],
        [customRestaurantListsData]
    );

    const [activeRestaurantListId, setActiveRestaurantListId] = useState(0);

    useEffect(() => {
        if (filterData?.defaultRestaurantListId) {
            setActiveRestaurantListId(filterData.defaultRestaurantListId);
        }
    }, [filterData?.defaultRestaurantListId]);

    const setActiveRestaurantList = useCallback((id: number) => {
        setActiveRestaurantListId(id);
    }, []);

    const value = useMemo<RestaurantMenuContextType>(() => ({
        rawFilterData: filterData ?? {},
        restaurantLists,
        tooltipsData,
        customRestaurantLists,
        activeRestaurantListId,
        isLoadingRestaurantLists,
        isLoadingCustomRestaurantLists,
        setActiveRestaurantList,
        createCustomRestaurantList: createList,
        updateCustomRestaurantList: updateList,
        deleteCustomRestaurantList: deleteList,
    }), [
        filterData,
        restaurantLists,
        customRestaurantLists,
        activeRestaurantListId,
        isLoadingRestaurantLists,
        isLoadingCustomRestaurantLists,
        setActiveRestaurantList,
        createList,
        updateList,
        deleteList,
    ]);

    if (isLoadingRestaurantLists) {
        return (
            <Loader
                isShown
                noBg
            />
        );
    }

    return (
        <RestaurantMenuContext.Provider value={value}>
            {children}
        </RestaurantMenuContext.Provider>
    );
};

export const useRestaurantMenu = () => {
    const context = useContext(RestaurantMenuContext);
    if (!context) {
        throw new Error('useRestaurantMenu must be used within RestaurantMenuProvider');
    }
    return context;
};

export default RestaurantMenuProvider;
